//
// Copyright (c) 2011-2021 by Curalate, Inc.
//

import React, { useLayoutEffect, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  animationTimeInMs?: number;
  shouldShow: boolean;
}

/**
 * The Fade molecule wraps any component you want to fade in and fade out
 * of existence in the DOM. The fade effect is triggered when `shouldShow`
 * is toggled. When `shouldShow` is false the Fade component and its
 * children will be removed from the DOM on completion of the animation.
 *
 */
export const Fade = ({
  animationTimeInMs = 300,
  children,
  className = '',
  shouldShow,
  ...divProps
}: Props) => {
  const [shouldRender, setRender] = useState<boolean>(true);

  // On first render we want to use shouldShow to determine whether or not to render
  // and set this inside of a useLayoutEffect so it is set before the browser paints
  // This prevents items with shouldShow = false from fading in and out on first render
  useLayoutEffect(() => {
    setRender(shouldShow);
  }, []);

  const onAnimationEnd = () => {
    setRender(shouldShow);
  };

  return shouldRender ? (
    <FadeWrapper
      animationTimeInMs={animationTimeInMs}
      className={className}
      onAnimationEnd={onAnimationEnd}
      shouldShow={shouldShow}
      {...divProps}
    >
      {children}
    </FadeWrapper>
  ) : null;
};

const FadeWrapper = styled.div<{
  animationTimeInMs: number;
  shouldShow: boolean;
}>(
  ({ animationTimeInMs, shouldShow }) => css`
    animation: ${shouldShow ? fadeIn : fadeOut} ${animationTimeInMs}ms;
  `,
);
const fadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
`;

export default Fade;
